import SearchTools, { LayoutMode } from "./SearchTools.tsx";
import SearchToggle from "./SearchToggle.tsx";
import { useIntl } from "react-intl";
import {
  Outlet,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import DefaultTemplate from "../../layout/content-templates/default/DefaultTemplate.tsx";
import ErrorBoundary from "../../../../common/core-ui/ErrorBoundary.tsx";
import OffScreen from "../../../../common/core-ui/OffScreen.tsx";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Search.module.scss";
import { getSearchQuery } from "../query.ts";
import useRecentSearches from "../typeahead/useRecentSearches.ts";
import PageTitle from "../../common/page-title/PageTitle.tsx";
import * as portals from "react-reverse-portal";
import useIsMediaLessThan from "../../../../common/core-ui/useIsMediaLessThan.ts";
import useIsHeaderSticky from "../../common/header/useIsHeaderSticky.ts";
import { useHomeOutletContext } from "../../layout/useHomeOutletContext.ts";
import useSampleSearchEligibility from "./useSampleSearchEligibility.ts";
import { UserPreferenceKey } from "common/api/decimal/methods/user.ts";
import useUserPreference from "routes/home/actions/user/useUserPreference.ts";
import throttle from "throttleit";
import clsx from "clsx";
import { useSampleSearchLog } from "../sample/useSampleSearchLog.ts";

function SearchInner() {
  const homeContext = useHomeOutletContext();

  // Always render outlet - start screens are now handled in subroutes
  return (
    <div className={styles.resultsArea}>
      <Outlet context={homeContext} />
    </div>
  );
}

export default function Search() {
  const [urlParams] = useSearchParams();
  const query = getSearchQuery(urlParams);
  const intl = useIntl();
  const title = intl.formatMessage({ defaultMessage: "Search" });
  const location = useLocation();
  const navigate = useNavigate();
  const log = useSampleSearchLog();
  const hasLoggedSampleViewRef = useRef(false);

  // Use the custom hook for sample search eligibility (sources data internally)
  const { isSampleSearchEnabled } = useSampleSearchEligibility();

  // Derive active tab from URL and sync with state
  // If sample search is disabled, always use library tab
  const activeTab: "library" | "sample" = !isSampleSearchEnabled
    ? "library"
    : location.pathname.startsWith("/search/sample")
    ? "sample"
    : "library";

  // Track when user first views Sample Search page
  useEffect(() => {
    if (activeTab === "sample" && !hasLoggedSampleViewRef.current) {
      log("sample_search_viewed");
      hasLoggedSampleViewRef.current = true;
    }
    // Omitting 'log' from deps: We only want to track when activeTab changes to "sample",
    // not when the log function changes (which happens when user.instructor.verified updates)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Redirect to library if user is on sample route but not eligible
  useEffect(() => {
    if (
      !isSampleSearchEnabled &&
      location.pathname.startsWith("/search/sample")
    ) {
      // User is not eligible but on sample route - redirect to library
      const searchParams = location.search;
      void navigate(`/search/library${searchParams}`, { replace: true });
    }
  }, [isSampleSearchEnabled, location.pathname, location.search, navigate]);

  // needs to be here to be persistent, see below about full UI reset
  const recentSearches = useRecentSearches(activeTab);

  const isSticky = useIsHeaderSticky();
  const portalNode = useMemo(() => portals.createHtmlPortalNode(), []);
  const [areSearchToolsOnScreen, setAreSearchToolsOnScreen] = useState(true);
  const searchBarDisplayMode: LayoutMode =
    isSticky && !areSearchToolsOnScreen ? "at-top" : "full-size";
  const needsSmallButtons =
    useIsMediaLessThan(1200) && searchBarDisplayMode === "at-top";

  const searchInputSentinelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const sentinel = searchInputSentinelRef.current;
    const header = headerRef.current;
    if (!sentinel || !header || !isSticky) {
      setAreSearchToolsOnScreen(true);
      return;
    }

    const checkPosition = throttle(() => {
      const sentinelRect = sentinel.getBoundingClientRect();
      const headerRect = header.getBoundingClientRect();
      const isBehindHeader = sentinelRect.bottom < headerRect.bottom;
      setAreSearchToolsOnScreen(!isBehindHeader);
    }, 150);

    checkPosition();

    // Zoom triggers a scroll adjustment too
    window.addEventListener("scroll", checkPosition);

    return () => {
      window.removeEventListener("scroll", checkPosition);
    };
  }, [isSticky]);

  // Track when user visits search to hide "New" tag permanently
  const [sampleSearchSeenDate, setSampleSearchSeenDate] = useUserPreference(
    UserPreferenceKey.LastSeenSampleSearch
  );

  useEffect(() => {
    if (searchBarDisplayMode === "at-top") {
      portalNode.element.style.maxWidth = "408px";
      portalNode.element.style.flexShrink = "1";
    } else {
      portalNode.element.style.maxWidth = "";
      portalNode.element.style.flexShrink = "";
    }
  }, [searchBarDisplayMode, portalNode]);

  // Set preference when user first visits search
  useEffect(() => {
    if (!sampleSearchSeenDate) {
      setSampleSearchSeenDate(new Date().toISOString());
    }
  }, [sampleSearchSeenDate, setSampleSearchSeenDate]);

  // We're using the DefaultTemplate in order to be able to use the panel
  return (
    <>
      <DefaultTemplate
        title={title}
        noInset
        headerConfig={{
          theme: "accent",
          bonusButtons:
            searchBarDisplayMode === "at-top" ? (
              <portals.OutPortal node={portalNode} />
            ) : null,
          forceCompactButtons: needsSmallButtons,
          ref: headerRef,
          allowImpersonationBanner: true,
        }}
      >
        <div
          className={clsx(
            styles.searchToolsContainer,
            isSampleSearchEnabled && styles.samplingToggleMobileView
          )}
        >
          {isSampleSearchEnabled && (
            <div className={styles.toggleWrapper}>
              <SearchToggle activeTab={activeTab} />
            </div>
          )}
          <div ref={searchInputSentinelRef}>
            {searchBarDisplayMode === "at-top" ? (
              <div className={styles.searchToolsPlaceholder} />
            ) : (
              <portals.OutPortal node={portalNode} />
            )}
          </div>
        </div>
        <OffScreen>
          <PageTitle title={title} />
        </OffScreen>
        {/* keys perform a full UI reset on query change */}
        <portals.InPortal node={portalNode}>
          <SearchTools
            key={query}
            query={query}
            recentSearches={recentSearches}
            displayMode={searchBarDisplayMode}
            activeTab={activeTab}
          />
        </portals.InPortal>
        <ErrorBoundary key={query}>
          <SearchInner />
        </ErrorBoundary>
      </DefaultTemplate>
    </>
  );
}
